import React, { useMemo } from 'react';
import { Box, Button, Text, useColorModeValue, Badge, HStack, Tooltip, Alert, AlertIcon, VStack, Icon } from '@chakra-ui/react';
import { detectCitationMarkers } from '../../../utils/citationDetectionUtils';
import { NotePencil, ArrowDown, BracketsCurly, Info } from '@phosphor-icons/react';

/**
 * Component to detect citations in text fields and show UI for adding new citations
 * 
 * @param {Object} props
 * @param {string} props.text - The text to analyze for citation markers
 * @param {Array} props.citations - Array of existing citations
 * @param {Function} props.onAddCitation - Function to call when user wants to add a citation
 * @param {boolean} props.showIfEmpty - Whether to show the add citation button even if no citations detected
 */
const TextFieldCitationDetector = ({ text, citations = [], onAddCitation, showIfEmpty = false }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const accentColor = useColorModeValue('yellow.500', 'yellow.400');
  
  // Detect citation markers in the text
  const markers = useMemo(() => {
    return detectCitationMarkers(text);
  }, [text]);
  
  // Check if markers are valid (references existing citations)
  const validationResults = useMemo(() => {
    if (!markers.length || !citations.length) return {};
    
    const results = {};
    markers.forEach(marker => {
      const citationNumber = Number(marker.citationNumber);
      // Citation is valid if a citation with that number exists
      results[marker.citationNumber] = citations.some(citation => 
        citation.number === citationNumber || 
        String(citation.number) === marker.citationNumber
      );
    });
    
    return results;
  }, [markers, citations]);
  
  // Don't render anything if no markers and not showIfEmpty
  if (markers.length === 0 && !showIfEmpty) {
    return null;
  }
  
  return (
    <Box mt={2} mb={4}>
      {/* Help text for citation format */}
      {showIfEmpty && (
        <Alert status="warning" variant="subtle" borderRadius="md" mb={3} py={2}>
          <AlertIcon as={Info} color="yellow.500" />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="medium">Citation Guide</Text>
            <Text fontSize="xs">
              Add citations using [number] format (e.g., [1]). Click "Add Citation" to create or manage sources.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Show citation markers found in the text */}
      {markers.length > 0 && (
        <Box mb={3} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor} bg={bgColor}>
          <HStack mb={2}>
            <Icon as={BracketsCurly} color="blue.500" />
            <Text fontSize="sm" fontWeight="medium">Citations Referenced:</Text>
          </HStack>
          
          <VStack align="stretch" spacing={2}>
            <HStack spacing={2} flexWrap="wrap">
              {markers.map((marker, idx) => {
                const isValid = validationResults[marker.citationNumber];
                return (
                  <Tooltip 
                    key={`${marker.index}-${idx}`}
                    label={isValid ? "Citation exists" : "Citation needs to be created"}
                    placement="top"
                  >
                    <Badge 
                      colorScheme={isValid ? "green" : "red"} 
                      variant="outline"
                    >
                      {marker.text}
                    </Badge>
                  </Tooltip>
                );
              })}
            </HStack>
            
            {markers.some(m => !validationResults[m.citationNumber]) && (
              <Text fontSize="xs" color="red.500">
                Some citations need to be created. Click "Manage Citations" below.
              </Text>
            )}
          </VStack>
        </Box>
      )}
      
      {/* Button to add a new citation */}
      <HStack>
        <Button 
          size="sm" 
          variant="outline" 
          leftIcon={markers.length > 0 ? <ArrowDown /> : <NotePencil />}
          colorScheme="teal"
          onClick={onAddCitation}
        >
          {markers.length > 0 ? "Manage Citations" : "Add Citation"}
        </Button>
        
        {markers.length === 0 && (
          <Tooltip label="Add citations to properly reference your sources">
            <Text fontSize="xs" color="gray.500">Use [1], [2], etc. to add citations</Text>
          </Tooltip>
        )}
      </HStack>
    </Box>
  );
};

export default TextFieldCitationDetector;
